import { useContext, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { AiFillCloseCircle, AiFillPlusCircle } from "react-icons/ai";
import { Reorder, useDragControls } from "framer-motion";
import {
  FaSortAlphaDownAlt,
  FaSortAlphaUp,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";
import { MdDragIndicator } from "react-icons/md";
import InteractiveContext from "../../context/InteractiveContext.jsx";
import UserContext from "../../context/UserContext.jsx";
import { getAuthToken } from "../../utils/helpers.js";
import { API_UpdateListItems } from "../../utils/api.js";

const SortableItem = ({ item, children, onDragEnd, className }) => {
  const controls = useDragControls();
  return <Reorder.Item value={item} dragListener={false} dragControls={controls} onDragEnd={onDragEnd} layout className={className}>
    <button type="button" aria-label="Drag to reorder" onPointerDown={(event) => controls.start(event)} className="touch-none cursor-grab p-2 calendar-accent"><MdDragIndicator size={22} /></button>{children}
  </Reorder.Item>;
};

const ListItems = ({ addItems, listId, items, expanded = false }) => {
  const { setSystemNotif, preferences, setLists } = useContext(UserContext);

  const [newItemText, setNewItemText] = useState("");
  const [indexes, setIndexes] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const itemsRef = useRef(null);
  useEffect(() => {
    const element = itemsRef.current;
    if (!element) return;
    const measure = () => setOverflowing(element.scrollHeight > 700);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [indexes, expanded, showAll]);

  useEffect(() => {
    const sorted = [...(items || [])].sort(
      (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
    );

    setIndexes(sorted);
  }, [items]);

  const saveItemsOrder = async (itemsToSave = indexes) => {
    const normalizedItems = itemsToSave.map((item, index) => ({
      ...item,
      orderIndex: index + 1,
    }));

    setLists((prev) =>
      prev.map((l) => {
        if (l.id === listId) {
          return { ...l, items: normalizedItems };
        }

        return l;
      }),
    );

    try {
      const token = getAuthToken();
      await API_UpdateListItems(listId, normalizedItems, token);
    } catch (err) {
      console.log("Error updating list item order");
      console.log(err);
    }
  };

  const updateOrderIndexes = (newOrder) => {
    saveItemsOrder(newOrder);
  };

  const sortItems = (type) => {
    const sorted = [...indexes];

    if (type === "az") {
      sorted.sort((a, b) => a.text.localeCompare(b.text));
    }

    if (type === "za") {
      sorted.sort((a, b) => b.text.localeCompare(a.text));
    }

    if (type === "largest") {
      sorted.sort((a, b) => b.text.length - a.text.length);
    }

    if (type === "smallest") {
      sorted.sort((a, b) => a.text.length - b.text.length);
    }

    saveItemsOrder(sorted);
  };

  const removeItem = async (itemId) => {
    const newItems = indexes.filter((i) => i.id !== itemId);

    setLists((prev) =>
      prev.map((l) => {
        if (l.id === listId) {
          return { ...l, items: newItems };
        }
        return l;
      }),
    );

    try {
      const token = getAuthToken();

      await API_UpdateListItems(listId, newItems, token);
    } catch (err) {
      console.log("Error updating list texts");
      console.log(err);
    }
  };

  const addNewItem = async () => {
    if (!newItemText) {
      const newNotif = {
        show: true,
        title: "No Item",
        text: "Please type in the list item you'd like to create",
        color: "bg-red-200",
        hasCancel: false,
        actions: [
          { text: "close", func: () => setSystemNotif({ show: false }) },
        ],
      };
      setSystemNotif(newNotif);
      return;
    }
    const newItem = {
      id: uuidv4(),
      text: newItemText,
      orderIndex: items.length + 1,
      complete: false,
    };

    const newItems = [...indexes, newItem];

    setNewItemText("");

    setLists((prev) =>
      prev.map((l) => {
        if (l.id === listId) {
          return { ...l, items: newItems };
        }
        return l;
      }),
    );

    try {
      const token = getAuthToken();
      await API_UpdateListItems(listId, newItems, token);
    } catch (err) {
      console.log("Error adding new list item to list");
      console.log(err);
    }
  };

  return (
    <div className="space-y-3">
      {/* Sort / Filter toolbar */}
      <div
        className={`
      rounded-3xl border shadow-sm p-3
      flex items-center gap-2 ml-3 flex-wrap
      ${preferences.darkMode ? "bg-white/5 border-white/10" : "bg-white border-black/10"}
    `}
      >
        <p
          className={`text-[11px] font-semibold tracking-wide mr-1 ${
            preferences.darkMode ? "text-white/55" : "text-slate-500"
          }`}
        >
          Sort
        </p>

        <button
          onClick={() => sortItems("az")}
          aria-label="Sort A to Z"
          title="A → Z"
        >
          <FaSortAlphaUp />
        </button>

        <button
          onClick={() => sortItems("za")}
          aria-label="Sort Z to A"
          title="Z → A"
        >
          <FaSortAlphaDownAlt />
        </button>

        <button
          onClick={() => sortItems("largest")}
          aria-label="Sort by largest first"
          title="Largest first"
        >
          <FaSortAmountDown />
        </button>

        <button
          onClick={() => sortItems("smallest")}
          aria-label="Sort by smallest first"
          title="Smallest first"
        >
          <FaSortAmountUp />
        </button>

        {/* Optional: space for future filter chips */}
        <div className="flex-1" />
      </div>

      {/* Add item row */}
      {addItems ? (
        <div
          className={`
        rounded-3xl border shadow-sm p-3
        flex items-center gap-3
        ${preferences.darkMode ? "bg-white/5 border-white/10" : "bg-white border-black/10"}
      `}
        >
          <form
            className="flex-1 min-w-0"
            onSubmit={(e) => {
              e.preventDefault();
              addNewItem();
            }}
          >
            <input
              type="text"
              className={`
            w-full px-4 py-3 rounded-2xl border shadow-inner outline-none text-sm font-semibold
            transition
            ${
              preferences.darkMode
                ? "bg-[#161616]/40 border-white/10 text-white placeholder:text-white/40 focus:bg-[#161616]/55"
                : "bg-black/[0.03] border-black/10 text-slate-900 placeholder:text-slate-400 focus:bg-black/[0.05]"
            }
          `}
              placeholder="New item…"
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              autoFocus
            />
          </form>

          <button
            type="button"
            onClick={() => addNewItem()}
            className={`
          h-12 w-12 grid place-items-center rounded-2xl border shadow-sm transition
          hover:shadow-md active:scale-[0.97]
          ${
            preferences.darkMode
              ? "bg-cyan-500/15 border-cyan-300/20 text-cyan-100 hover:bg-cyan-500/20"
              : "bg-cyan-50 border-cyan-200 text-cyan-700 hover:bg-cyan-100"
          }
        `}
            aria-label="Add item"
          >
            <AiFillPlusCircle className="text-2xl" />
          </button>
        </div>
      ) : null}

      {/* List */}
      <Reorder.Group
        axis="y"
        ref={itemsRef}
        values={indexes}
        onReorder={setIndexes}
        className={`
          ${expanded || showAll ? "overflow-visible" : "max-h-[700px] overflow-hidden"} pt-3 pr-1
        `}
      >
        {indexes?.map((listItem, index) => (
          <SortableItem
            key={listItem.id}
            item={listItem}
            onDragEnd={() => saveItemsOrder(indexes)}
            className={`
              relative cursor-grab select-none mb-2
              rounded-3xl border shadow-sm
              px-4 py-4
              flex items-center justify-between gap-3
              transition-colors
              ${
                preferences.darkMode
                  ? "bg-white/5 border-white/10 hover:bg-white/7"
                  : "bg-white border-black/10 hover:bg-black/[0.02]"
              }
            `}
          >
            {/* Index badge */}
            <div
              className={`
            absolute -top-2 -left-2
            h-7 min-w-[28px] px-2
            grid place-items-center
            rounded-2xl border shadow-md text-[11px] font-bold
            ${
              preferences.darkMode
                ? "bg-white/10 border-white/15 text-white/80"
                : "bg-white border-black/10 text-slate-700"
            }
          `}
            >
              {index + 1}
            </div>

            {/* Text */}
            <div className="min-w-0">
              <p
                className={`text-sm font-semibold break-words ${preferences.darkMode ? "text-white/85" : "text-slate-900"}`}
              >
                {listItem.text}
              </p>
            </div>

            {/* Remove */}
            <button
              type="button"
              onClick={() => removeItem(listItem.id)}
              className={`
            flex-shrink-0 h-10 w-10 grid place-items-center rounded-2xl border shadow-sm transition
            hover:shadow-md active:scale-[0.97]
            ${
              preferences.darkMode
                ? "bg-white/5 border-white/10 text-white/60 hover:text-rose-200 hover:bg-white/10"
                : "bg-black/[0.03] border-black/10 text-slate-500 hover:text-rose-600 hover:bg-black/[0.06]"
            }
          `}
              aria-label="Remove item"
            >
              <AiFillCloseCircle className="text-xl" />
            </button>
          </SortableItem>
        ))}
      </Reorder.Group>
      {!expanded && (overflowing || showAll) && <button type="button" onClick={() => setShowAll((value) => !value)} className="calendar-action w-full px-4 py-2 text-sm">{showAll ? "Show less" : "View more"}</button>}
    </div>
  );
};

export default ListItems;
